import React, { useMemo, useRef } from "react";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
// import { DA_CartPriceData } from "@danishagro/shared/src/interfaces/price.interface";
import Skeleton from "react-loading-skeleton";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_BasketPriceData } from "@danishagro/shared/src/interfaces/basket.interface";
import { DA_InlineLink } from "@danishagro/shared/src/components/atoms/InlineLink/InlineLink.component";
import { getLoginLink } from "@danishagro/shared/src/helpers/getLoginLink.helper";
import { usePrices } from "@danishagro/shared/src/hooks/usePrices.hook";
import S from "./CartTotal.module.scss";

export interface DA_CartTotalProps {
    prices?: DA_BasketPriceData;
    loggedIn?: boolean;
    hideButtons?: boolean;
    fullWidth?: boolean;
    description?: string;
    className?: string;
}

export const DA_CartTotal = ({
    prices,
    loggedIn,
    hideButtons,
    fullWidth,
    description,
    className,
}: DA_CartTotalProps) => {
    const { getDictionaryString } = useTranslations();
    const { formatPrice } = usePrices();

    const textOptions = useRef({ uppercaseFirst: true }).current;
    const dontShowFallback = useRef({ dontShowFallback: true }).current;
    const text = useMemo(
        () => ({
            subtotal: getDictionaryString("cart.cartTotal.subTotal", textOptions),
            totalExclVat: getDictionaryString("cart.cartTotal.totalExclVat", textOptions),
            vat: getDictionaryString("cart.cartTotal.vat", textOptions),
            savings: getDictionaryString("cart.cartTotal.savings", textOptions),
            shipping: getDictionaryString("cart.cartTotal.shipping", textOptions),
            total: getDictionaryString("cart.cartTotal.total", textOptions),
            comment: getDictionaryString("order delivery comment", dontShowFallback),
            orderAsUser: getDictionaryString("order on account"),
            loginAndCheckout: getDictionaryString("cart.cartTotal.loginAndCheckout", textOptions),
            continueToCheckout: getDictionaryString("continue to checkout"),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getDictionaryString, textOptions]
    );
    const formattedPrices = useMemo(
        () => ({
            subTotalPrice: prices ? formatPrice(prices?.subtotal) : <Skeleton width={80} />,
            totalPriceExVat: prices ? (
                formatPrice(prices?.totalPriceWithoutVat)
            ) : (
                <Skeleton width={80} />
            ),
            totalSavings: prices ? formatPrice(prices.totalSavings) : <Skeleton width={50} />,
            shippingPrice: prices ? formatPrice(prices.shipping) : <Skeleton width={50} />,
            vatPrice: prices ? formatPrice(prices.vat) : <Skeleton width={50} />,
            totalPrice: prices ? formatPrice(prices?.totalPriceWithVat) : <Skeleton width={80} />,
        }),
        [prices, formatPrice]
    );

    // Check for RoadTax
    function hasRoadTax(lines?: Array<{ roadTax?: number }>) {
        return !!lines?.some((line) => typeof line.roadTax !== "undefined");
    }

    return (
        <section className={className}>
            <div className={cn(S.overview, fullWidth && S.fullWidth)}>
                <dl>
                    <dt className={S.borderBottom}>{text.subtotal}:</dt>
                    <dd className={S.borderBottom}>{formattedPrices.subTotalPrice}</dd>

                    <dt className={S.gray}>{text.savings}:</dt>
                    <dd className={S.gray}>{formattedPrices.totalSavings}</dd>

                    <dt>{text.shipping}:</dt>
                    <dd>{formattedPrices.shippingPrice}</dd>

                    <dt className={S.total}>{text.total}:</dt>
                    <dd className={S.total}>{formattedPrices.totalPrice}</dd>

                    <dt>{text.vat} (25 %):</dt>
                    <dd>{formattedPrices.vatPrice}</dd>

                    {/* {prices?.charges
                                ? prices.charges.map((charge, index) =>
                                      charge.chargeCode.trim() !== "" ? (
                                          <React.Fragment key={index}>
                                              <dt>{getDictionaryString(charge.chargeCode)}:</dt>
                                              <dd>{formatPrice(charge.totalCalculated)}</dd>
                                          </React.Fragment>
                                      ) : null
                                  )
                                : null} */}

                    <dt className={S.totalExclVat}>
                        {text.totalExclVat}
                        <span className={S.roadTax}>
                            {hasRoadTax(prices?.lines) && ` (${getDictionaryString("IncRoadTax")})`}
                        </span>
                        :
                    </dt>
                    <dd className={S.totalExclVat}>{formattedPrices.totalPriceExVat}</dd>

                    <div className={S.comment}>{text.comment}</div>
                </dl>

                {!hideButtons && (
                    <div className={S.actions}>
                        <div className={S.user}>
                            <DA_Button
                                title={text.continueToCheckout}
                                // href={loggedIn ? "/checkout" : getLoginLink()}

                                // TODO: get checkout link from umbraco
                                href="/checkout"
                            />
                        </div>

                        {/* TODO: Enable this again when possible - The CSS class is also commented out */}
                        {!loggedIn && (
                            <div className={S.guest}>
                                <DA_InlineLink
                                    title={text.loginAndCheckout}
                                    href={loggedIn ? "/checkout" : getLoginLink("B2C")}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {description && (
                <div className={S.description}>
                    <DA_Text smallText noPadding html={description} />
                </div>
            )}
        </section>
    );
};
